import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { PortalDsAlert } from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { cn } from 'src/lib/utils';
import {
  isPortalSyncNotConfiguredError,
  useGetLaunchedCampaignQuery,
  useGetSelfServiceCampaignDetailQuery,
} from '../endpoints/launched-campaigns.endpoints';
import type { CampaignRequestAction } from '../types/campaigns.types';
import { mapLaunchedCampaignDetail } from '../utils/campaigns.mappers';
import { formatLaunchedAt } from '../utils/campaigns.utils';
import { CampaignRequestActions } from './campaign-request-actions';
import { CampaignStatusBadge } from './campaign-status-badge';
import { SelfServiceCampaignDetailSection } from './self-service-campaign-detail-section';
import { SyncStatusIndicator } from './sync-status-indicator';

interface CampaignRequestDrawerProps {
  campaignId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAction: (campaignId: string, action: CampaignRequestAction) => Promise<void>;
  actionsDisabled?: boolean;
}

function DetailField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <dt className="text-[12px] font-medium uppercase tracking-[0.04em] text-[#9CA3AF]">
        {label}
      </dt>
      <dd className="text-[14px] text-[#0D0F1A]">
        {children}
      </dd>
    </div>
  );
}

function formatOptionalDate(isoDate: string | null): string {
  if (!isoDate) {
    return '—';
  }
  return formatLaunchedAt(isoDate);
}

export function CampaignRequestDrawer({
  campaignId,
  open,
  onOpenChange,
  onAction,
  actionsDisabled = false,
}: CampaignRequestDrawerProps) {
  const {
    data,
    isLoading: isPortalLoading,
    isError: isPortalError,
  } = useGetLaunchedCampaignQuery(campaignId ?? '', {
    skip: !open || !campaignId,
  });

  const campaign = data ? mapLaunchedCampaignDetail(data) : null;
  const hasExternalId = Boolean(campaign?.externalId);

  const {
    data: selfServiceDetail,
    isLoading: isSelfServiceLoading,
    isError: isSelfServiceError,
    error: selfServiceError,
  } = useGetSelfServiceCampaignDetailQuery(campaignId ?? '', {
    skip: !open || !campaignId || !hasExternalId,
  });

  const isSyncNotConfigured = isSelfServiceError
    && isPortalSyncNotConfiguredError(selfServiceError);

  const drawerTitle = isPortalLoading
    ? 'Loading request…'
    : selfServiceDetail?.name ?? campaign?.name ?? 'Campaign request';

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="!w-full sm:!w-[680px] !max-w-none flex flex-col overflow-hidden bg-white p-0"
      >
        <SheetHeader className="border-b border-[#EDEEF2] px-6 py-5 text-left">
          <SheetTitle className="text-[18px] font-semibold tracking-[-0.02em] text-[#0D0F1A]">
            {drawerTitle}
          </SheetTitle>
          {campaign ? (
            <div className="mt-3 flex items-center justify-between gap-4">
              <CampaignStatusBadge status={campaign.status} />
              <CampaignRequestActions
                campaignId={campaign.id}
                status={campaign.status}
                assignedToEmail={campaign.assignedToEmail}
                disabled={actionsDisabled}
                onAction={onAction}
              />
            </div>
          ) : null}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isPortalLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[#5E6278]">
              <Loader2
                className="size-5 animate-spin"
                aria-hidden
              />
              <span>
                Loading request…
              </span>
            </div>
          ) : isPortalError || !campaign ? (
            <PortalDsAlert
              variant="danger"
              title="Could not load request"
            >
              This request could not be loaded. Try closing and opening it again.
            </PortalDsAlert>
          ) : (
            <div className="flex flex-col gap-8">
              <SelfServiceCampaignDetailSection
                detail={selfServiceDetail}
                isLoading={isSelfServiceLoading}
                isError={isSelfServiceError && !isSyncNotConfigured}
                isSyncNotConfigured={isSyncNotConfigured}
                hasExternalId={hasExternalId}
              />

              <section>
                <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#5E6278]">
                  Portal request
                </h2>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <DetailField label="Portal client">
                    {campaign.client ?? 'Unassigned'}
                  </DetailField>
                  <DetailField label="Launched at">
                    <span className="font-mono text-[13px]">
                      {formatLaunchedAt(campaign.launchedAt)}
                    </span>
                  </DetailField>
                  <DetailField label="Assigned to">
                    {campaign.assignedToEmail ?? 'Unassigned'}
                  </DetailField>
                  <DetailField label="Assigned at">
                    {formatOptionalDate(campaign.assignedAt)}
                  </DetailField>
                  <DetailField label="Status updated at">
                    {formatOptionalDate(campaign.statusUpdatedAt)}
                  </DetailField>
                  <DetailField label="Status updated by">
                    {campaign.statusUpdatedByEmail ?? '—'}
                  </DetailField>
                </dl>
              </section>

              <section>
                <h2 className="mb-4 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#5E6278]">
                  Sync status
                </h2>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <DetailField label="Monday sync">
                    <SyncStatusIndicator
                      status={campaign.mondaySync}
                      label="Monday sync"
                    />
                  </DetailField>
                  <DetailField label="Monday item ID">
                    {campaign.mondayItemId ?? '—'}
                  </DetailField>
                  <DetailField label="Slack notification">
                    <SyncStatusIndicator
                      status={campaign.slackSync}
                      label="Slack notification"
                    />
                  </DetailField>
                </dl>
                {Object.keys(campaign.notificationErrors).length > 0 ? (
                  <div className="mt-4 rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-4">
                    <p className="mb-2 text-[13px] font-medium text-[#DC2626]">
                      Notification errors
                    </p>
                    <ul className="space-y-1 text-[13px] text-[#7F1D1D]">
                      {Object.entries(campaign.notificationErrors).map(([key, message]) => (
                        <li key={key}>
                          <span className="font-medium capitalize">
                            {key}
                            :
                          </span>
                          {' '}
                          {message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
